<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useOrderTemplateListStore } from '@/orderTemplateModule/stores/useOrderTemplateListStore';
import OrderTemplateUploadPanel from '@/orderTemplateModule/components/OrderTemplateUploadPanel.vue';
import OrderTemplateTableCard from '@/orderTemplateModule/components/OrderTemplateTableCard.vue';
import SettingsPageTitle from '@/settingsModule/components/SettingsPageTitle.vue';
import { diContainer } from '@/coreContainer/container';
import type { OrderTemplateUploadUrl } from '@/orderTemplateModule/domains/valueObjects/presignedUrl';
import { useRouter } from 'vue-router';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { i18n } from '@/i18n';
import { resolveOrderTemplateErrorMessage } from '@/orderTemplateModule/utils/errorMessages';

const t = i18n.t;
const listStore = useOrderTemplateListStore();
const { templates, loading, error } = storeToRefs(listStore);
const { deleteTemplate, fetchTemplates } = listStore;

const uploadLoading = ref(false);
const uploadMessage = ref('');
const uploadResetKey = ref(0);
const uploadMessageType = ref<'info' | 'error'>('info');
const lastUpload = ref<{ templateId: string; fileName: string; storagePath: string } | null>(null);
const tableNotice = ref<{ type: 'info' | 'error'; text: string } | null>(null);
const deletingTemplateIds = ref<string[]>([]);
const router = useRouter();

const DEFAULT_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const TEMPLATE_DRAFT_PREFIX = 'orderTemplateDraft:';

/**
 * Presigned URLをブラウザから到達可能なホストへ変換する
 * @param rawUrl - サーバーから受け取ったURL
 */
const resolveUploadUrl = (rawUrl: string): string => {
  try {
    const parsed = new URL(rawUrl);
    if (parsed.hostname === 'minio') {
      parsed.hostname = window.location.hostname;
    }
    return parsed.toString();
  } catch {
    return rawUrl;
  }
};

/**
 * Presigned POSTを使ってファイルをS3へアップロードする
 * @param uploadUrl - 発行済みPresigned URL
 * @param file - アップロード対象ファイル
 */
const uploadToS3 = async (uploadUrl: OrderTemplateUploadUrl, file: File): Promise<void> => {
  const formData = new FormData();
  Object.entries(uploadUrl.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', file);

  const targetUrl = resolveUploadUrl(uploadUrl.url);
  const response = await fetch(targetUrl, {
    method: 'POST',
    body: formData,
    mode: 'cors',
    referrerPolicy: 'no-referrer',
  });

  if (!response.ok) {
    throw new Error(t('S3アップロードに失敗した（status: {status}）', { status: response.status }) as string);
  }
};

/**
 * ファイル選択時のハンドラ
 * @param file - ユーザーが選択したファイル
 */
const handleFileSelected = (file: File) => {
  uploadMessage.value = '';
  uploadMessageType.value = 'info';
};

/**
 * サポート外ファイル選択時のハンドラ
 * @param reason - 拒否理由
 */
const handleInvalidFile = (reason: string) => {
  uploadMessage.value = reason;
  uploadMessageType.value = 'error';
};

/**
 * ファイル選択をキャンセルする
 */
const handleCancel = () => {
  uploadMessage.value = '';
  uploadMessageType.value = 'info';
};

/**
 * 書式読込アクションのハンドラ
 * @param file - 読み込み対象のファイル
 */
const handleSubmit = async (file: File) => {
  uploadLoading.value = true;
  try {
    uploadMessage.value = '';
    uploadMessageType.value = 'info';
    const requestPayload = {
      fileName: file.name,
      contentType: file.type || DEFAULT_CONTENT_TYPE,
      fileSize: file.size,
    };

    const uploadUrlResult = await diContainer.createOrderTemplateUploadUrlUseCase.Execute(requestPayload);
    if (uploadUrlResult.status.type !== 'success') {
      console.error('Failed to create order template upload URL', uploadUrlResult.status.error);
      uploadMessageType.value = 'error';
      uploadMessage.value = resolveOrderTemplateErrorMessage(
        uploadUrlResult.status.error,
        t('ファイルの読み込み準備に失敗しました。しばらくしてから再実行してください。') as string
      );
      return;
    }

    const uploadUrl = uploadUrlResult.status.data;
    if (!uploadUrl.templateId) {
      throw new Error(t('テンプレートIDの取得に失敗しました') as string);
    }
    const templateId = uploadUrl.templateId;
    if (!uploadUrl.s3Key.endsWith(`${templateId}.xlsx`) || !uploadUrl.s3Key.startsWith('order_templates/')) {
      throw new Error(t('アップロード先キーが想定と異なる') as string);
    }

    await uploadToS3(uploadUrl, file);

    uploadMessageType.value = 'info';
    uploadMessage.value = t(
      'テンプレートID「{templateId}」でファイルをS3にアップロードした。保存APIでの登録はマッピング入力後に実行する。',
      {
        templateId,
      }
    ) as string;
    uploadResetKey.value += 1;
    lastUpload.value = {
      templateId,
      fileName: file.name,
      storagePath: uploadUrl.s3Key,
    };
    try {
      const payload = JSON.stringify({ templateId, fileName: file.name, storagePath: uploadUrl.s3Key });
      sessionStorage.setItem(`${TEMPLATE_DRAFT_PREFIX}${templateId}`, payload);
    } catch {
      // セッションストレージが利用できない場合も処理を継続する
    }

    handleNavigateToCreate();
  } catch (error) {
    console.error('Order template upload failed', error);
    uploadMessageType.value = 'error';
    uploadMessage.value = t('ファイルの読み込みに失敗しました。時間をおいて再度お試しください。') as string;
  } finally {
    uploadLoading.value = false;
  }
};

/**
 * テンプレート登録ページへ遷移する
 */
const handleNavigateToCreate = () => {
  if (!lastUpload.value) {
    return;
  }
  router.push({
    name: 'order-template-create',
    query: {
      templateId: lastUpload.value.templateId,
    },
  });
};

const orderTableError = computed(() => {
  if (!error.value) {
    return null;
  }
  return resolveOrderTemplateErrorMessage(error.value, t('書式一覧の取得に失敗しました。時間をおいて再度お試しください。') as string);
});

onMounted(async () => {
  await fetchTemplates();
});

/**
 * テンプレート名を削除ダイアログ用に取得する
 * @param templateId - 対象テンプレートID
 */
const getTemplateDisplayLabel = (templateId: string): string => {
  const target = templates.value.find((template) => template.templateId === templateId);
  return target?.fileOriginalName ?? templateId;
};

/**
 * テンプレート削除モーダルでユーザーに確認を行う
 * @param displayLabel - 表示名
 */
const confirmTemplateDeletion = async (displayLabel: string): Promise<boolean> => {
  return showInfoModal({
    modalTitle: t('テンプレート削除') as string,
    modalDescription: t('テンプレート「{label}」を削除します。よろしいですか？', {
      label: displayLabel,
    }) as string,
    backButtonLabel: t('キャンセル') as string,
    nextButtonLabel: t('削除する') as string,
    icon: 'error',
    colorStyle: 'error',
    enableOutsideClick: false,
  });
};

/**
 * 削除処理完了後のメッセージを設定する
 * @param text - 表示するメッセージ
 * @param type - メッセージ種別
 */
const setTableNotice = (text: string, type: 'info' | 'error'): void => {
  tableNotice.value = { type, text };
};

/**
 * テンプレート削除を実行する
 * @param templateId - 削除対象のテンプレートID
 */
const handleDeleteTemplate = async (templateId: string): Promise<void> => {
  if (deletingTemplateIds.value.includes(templateId)) {
    return;
  }

  const displayLabel = getTemplateDisplayLabel(templateId);
  const confirmed = await confirmTemplateDeletion(displayLabel);
  if (!confirmed) {
    return;
  }

  deletingTemplateIds.value = [...deletingTemplateIds.value, templateId];
  try {
    const result = await deleteTemplate(templateId);
    if (result.status.type === 'success') {
      setTableNotice(t('テンプレート「{label}」を削除しました。', { label: displayLabel }) as string, 'info');
    } else {
      console.error('Failed to delete order template', result.status.error);
      setTableNotice(
        resolveOrderTemplateErrorMessage(
          result.status.error,
          t('テンプレートの削除に失敗しました。時間をおいて再度お試しください。') as string
        ),
        'error'
      );
    }
  } finally {
    deletingTemplateIds.value = deletingTemplateIds.value.filter((id) => id !== templateId);
  }
};
</script>

<template>
  <section class="order-template-page">
    <header class="order-template-page__header">
      <SettingsPageTitle :label="t('設定 > 書式・帳票')" />
    </header>

    <OrderTemplateUploadPanel
      :intro-message="t('発注書や帳票の出力書式を登録・編集します')"
      :loading="uploadLoading"
      :reset-key="uploadResetKey"
      @file-selected="handleFileSelected"
      @invalid-file="handleInvalidFile"
      @cancel="handleCancel"
      @submit="handleSubmit"
    />
    <p
      v-if="uploadMessage"
      :class="['order-template-page__notice', { 'order-template-page__notice--error': uploadMessageType === 'error' }]"
    >
      {{ uploadMessage }}
    </p>
    <div v-if="lastUpload" class="order-template-page__actions">
      <button class="order-template-page__action-button" @click="handleNavigateToCreate">
        {{ t('アップロードしたテンプレートを登録する') }}
      </button>
    </div>

    <div class="order-template-page__tables">
      <p
        v-if="tableNotice"
        :class="['order-template-page__notice', { 'order-template-page__notice--error': tableNotice.type === 'error' }]"
      >
        {{ tableNotice.text }}
      </p>
      <OrderTemplateTableCard
        :title="t('発注出力書式一覧')"
        :templates="templates"
        :loading="loading"
        :error-message="orderTableError"
        :processing-template-ids="deletingTemplateIds"
        @delete-click="handleDeleteTemplate"
      />
    </div>
  </section>
</template>

<style scoped lang="scss">
.order-template-page {
  display: grid;
  gap: 24px;
  padding: 24px;

  &__header {
    display: grid;
    gap: 0;
  }

  &__notice {
    margin: 0;
    padding: 12px 16px;
    border-radius: 4px;
    background-color: $surfaceDefault;
    color: $textSecondary;

    &--error {
      background-color: $semanticErrorSubdued;
      color: $semanticError;
    }
  }

  &__tables {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
  }

  &__actions {
    display: grid;
    justify-items: start;
  }

  &__action-button {
    padding: 12px 20px;
    border-radius: 4px;
    border: 1px solid transparent;
    background-color: $semanticPrimary;
    color: $semanticWhite;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.9;
    }
  }
}
</style>
